import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { setLoading } from '../../../store/actions';
import { getPropertyList } from '../../Inventory/pages/InventoryOrders/utils/api';
import { getChatbotConfigs, deleteChatbotConfig, getChatbotAnalytics } from './Components/FormikHelpers/Api';
import { ChatbotConfigModal } from './Components/ChatbotConfigModal';
import { DeleteChatbotConfigModal } from './Components/DeleteChatbotConfigModal';
import { ChatbotLeadsTable } from './Components/ChatbotLeadsTable';
import { ChatWidget } from './Components/ChatWidget';
import './Components/chat-bot.scss';

const Chatbot = () => {
  const dispatch = useDispatch();

  const [configs, setConfigs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [properties, setProperties] = useState([]);
  const [activeTab, setActiveTab] = useState('configs');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [previewToken, setPreviewToken] = useState('');

  const [configModal, setConfigModal] = useState({ open: false, mode: 'add', config: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, config: null });
  const [refreshKey, setRefreshKey] = useState(0);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchConfigs = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const res = await getChatbotConfigs();
      setConfigs(res?.data || []);
    } catch {
      toast.error('Failed to load chatbot configs');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await getChatbotAnalytics();
      setAnalytics(res?.data || null);
    } catch { }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    try {
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch {
      Swal.fire('Error', 'Failed to load properties', 'error');
    }
  }, [getUserFromToken]);

  useEffect(() => {
    fetchProperties();
    fetchConfigs();
    fetchAnalytics();
  }, [fetchProperties, fetchConfigs, fetchAnalytics]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.config?._id) return;
    try {
      dispatch(setLoading(true));
      await deleteChatbotConfig(deleteModal.config._id);
      toast.success('Chatbot config deleted');
      setRefreshKey((k) => k + 1);
      fetchConfigs();
    } finally {
      dispatch(setLoading(false));
      setDeleteModal({ open: false, config: null });
    }
  }, [dispatch, deleteModal.config, fetchConfigs]);

  const handleSaved = useCallback(() => {
    fetchConfigs();
    fetchAnalytics();
    setRefreshKey((k) => k + 1);
  }, [fetchConfigs, fetchAnalytics]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  return (
    <div className="page-content">
      <MetaTags><title>Rentdigicare | Chatbot</title></MetaTags>
      <div className="container-fluid mb-2">
        <Breadcrumbs title="Settings" breadcrumbItem="Chatbot" />

        {analytics && (
          <div className="cb-page__stats">
            {[
              { icon: 'users', label: 'Total Leads', value: analytics.total_leads, color: '#1D4ED8' },
              { icon: 'bell', label: 'New Leads', value: analytics.new_leads, color: '#EF4444' },
              { icon: 'message-circle', label: 'Sessions', value: analytics.total_sessions, color: '#0D9488' },
              { icon: 'trending-up', label: 'Conversion', value: analytics.conversion_rate, color: '#7C3AED' },
            ].map(({ icon, label, value, color }) => (
              <div key={label} className="cb-page__stat-card">
                <div className="cb-page__stat-icon" style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
                  <FeatherIcon icon={icon} size={18} color={color} />
                </div>
                <div>
                  <div className="cb-page__stat-label">{label}</div>
                  <div className="cb-page__stat-value">{value ?? '—'}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="cb-page__card">

          <div className="cb-page__tabs">
            <div className="cb-page__tab-group">
              {[
                { key: 'configs', label: 'Configs' },
                { key: 'leads', label: 'Leads' },
                { key: 'preview', label: 'Preview' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`cb-page__tab${activeTab === key ? ' cb-page__tab--active' : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                  {key === 'leads' && analytics?.new_leads > 0 && (
                    <span className="cb-page__tab__badge">{analytics.new_leads}</span>
                  )}
                </button>
              ))}
            </div>

            {activeTab === 'configs' && (
              <div className="cb-page__tab-actions">
                <button
                  type="button"
                  className="ri-btn ri-btn--primary"
                  onClick={() => setConfigModal({ open: true, mode: 'add', config: null })}
                >
                  <FeatherIcon icon="plus" size={14} /> New Chatbot
                </button>
              </div>
            )}
          </div>

          <div className="cb-page__body">

            {activeTab === 'configs' && (
              <>
                {configs.length === 0 ? (
                  <div className="cb-config-empty">
                    <div className="cb-config-empty__icon">
                      <FeatherIcon icon="message-square" size={28} color="#3B82F6" />
                    </div>
                    <div className="cb-config-empty__title">No Chatbot Configs</div>
                    <div className="cb-config-empty__sub">Click "New Chatbot" to create your first chatbot for a property.</div>
                  </div>
                ) : (
                  <div className="cb-config-grid">
                    {configs.map((cfg) => (
                      <div key={cfg._id} className="cb-config-card">
                        <div
                          className="cb-config-card__accent-bar"
                          style={{ background: `linear-gradient(90deg, ${cfg.branding?.primary_color || '#1D4ED8'}, ${cfg.branding?.secondary_color || '#3B82F6'})` }}
                        />

                        <div className="cb-config-card__header">
                          <div
                            className="cb-config-card__avatar"
                            style={{ background: `${cfg.branding?.primary_color || '#1D4ED8'}18`, border: `1px solid ${cfg.branding?.primary_color || '#1D4ED8'}30` }}
                          >
                            {cfg.branding?.logo_url
                              ? <img src={cfg.branding.logo_url} alt="" />
                              : cfg.branding?.launcher_icon || '💬'}
                          </div>
                          <div className="cb-config-card__meta">
                            <div className="cb-config-card__name">{cfg.branding?.bot_name || 'Assistant'}</div>
                            <div className="cb-config-card__slug">/{cfg.chat_slug}</div>
                          </div>
                          <div className={`cb-config-card__status-dot cb-config-card__status-dot--${cfg.is_active ? 'active' : 'inactive'}`} />
                        </div>

                        <div className="cb-config-card__token-row">
                          <div className="cb-config-card__token-label">Embed Token</div>
                          <div className="cb-config-card__token-wrap">
                            <code className="cb-config-card__token-code">{cfg.token}</code>
                            <button type="button" className="cb-config-card__copy-btn" onClick={() => copyToClipboard(cfg.token)}>
                              <FeatherIcon icon="copy" size={13} />
                            </button>
                          </div>
                        </div>

                        <div className="cb-config-card__tags">
                          <span className="cb-config-card__tag cb-config-card__tag--plan">{cfg.plan}</span>
                          {cfg.ai?.enabled && (
                            <span className="cb-config-card__tag cb-config-card__tag--ai">AI enabled</span>
                          )}
                          {cfg.integrations?.suite_api_enabled && (
                            <span className="cb-config-card__tag cb-config-card__tag--suite">Suite lookup</span>
                          )}
                          {cfg.integrations?.booking_url && (
                            <span className="cb-config-card__tag cb-config-card__tag--booking">Booking</span>
                          )}
                        </div>

                        <div className="cb-config-card__actions">
                          <button type="button" className="cb-config-card__action-btn cb-config-card__action-btn--edit"
                            onClick={() => setConfigModal({ open: true, mode: 'edit', config: cfg })}>
                            <FeatherIcon icon="edit-2" size={12} /> Edit
                          </button>
                          <button type="button" className="cb-config-card__action-btn cb-config-card__action-btn--preview"
                            onClick={() => { setPreviewToken(cfg.token); setActiveTab('preview'); }}>
                            <FeatherIcon icon="eye" size={12} /> Preview
                          </button>
                          <button type="button" className="cb-config-card__action-btn cb-config-card__action-btn--delete"
                            onClick={() => setDeleteModal({ open: true, config: cfg })}>
                            <FeatherIcon icon="trash-2" size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'leads' && (
              <div>
                <div className="cb-leads__filter-row">
                  <label className="cb-leads__filter-label">Filter by chatbot:</label>
                  <select
                    className="cb-leads__filter-select"
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                  >
                    <option value="">All Chatbots</option>
                    {configs.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.branding?.bot_name || c.chat_slug}
                      </option>
                    ))}
                  </select>
                </div>
                <ChatbotLeadsTable
                  key={refreshKey}
                  configId={selectedConfigId}
                  configs={configs}
                />
              </div>
            )}

            {activeTab === 'preview' && (
              <div>
                <div className="cb-preview__selector-row">
                  <label className="cb-preview__selector-label">Preview chatbot:</label>
                  <select
                    className="cb-preview__selector-select"
                    value={previewToken}
                    onChange={(e) => setPreviewToken(e.target.value)}
                  >
                    <option value="">Select a chatbot…</option>
                    {configs.map((c) => (
                      <option key={c._id} value={c.token}>
                        {c.branding?.bot_name || c.chat_slug}
                      </option>
                    ))}
                  </select>
                </div>

                {previewToken ? (
                  <div className="cb-preview__canvas">
                    <p className="cb-preview__canvas-hint">
                      The chatbot widget will appear in the bottom-right corner. Click the bubble to open it.
                    </p>

                    <div className="cb-preview__embed-box">
                      <div className="cb-preview__embed-title">Embed Code</div>
                      <code className="cb-preview__embed-code">
                        {`<script src="https://rentdigicare.ca/api/chatbot.js" data-token="${previewToken}"></script>`}
                      </code>
                      <button
                        type="button"
                        className="cb-preview__embed-copy"
                        onClick={() => copyToClipboard(`<script src="https://rentdigicare.ca/api/chatbot.js" data-token="${previewToken}"></script>`)}
                      >
                        <FeatherIcon icon="copy" size={11} /> Copy
                      </button>
                    </div>

                    <ChatWidget token={previewToken} />
                  </div>
                ) : (
                  <div className="cb-preview__empty">
                    <FeatherIcon icon="eye" size={32} color="#CBD5E1" />
                    <span>Select a chatbot to preview it</span>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        <ChatbotConfigModal
          mode={configModal.mode}
          open={configModal.open}
          onClose={() => setConfigModal({ open: false, mode: 'add', config: null })}
          onSaved={handleSaved}
          existingConfig={configModal.config}
          propertyList={properties}
        />

        <DeleteChatbotConfigModal
          open={deleteModal.open}
          onClose={() => setDeleteModal({ open: false, config: null })}
          onDelete={handleDeleteConfirm}
          config={deleteModal.config}
        />

      </div>
    </div>
  );
};

export { Chatbot };